import { useState, useEffect, useCallback } from 'react';
// import type { SolutionListItem } from '../Types/diagram-type';
import { httpService } from '../../../utility/httpService';
import { httpConstants } from '../../../constants';
import { API_BASE } from '../../../config/env';
import { useGraphQLQuery } from '../../../customHooks/useGraphQL';
import { GET_AI_SOLUTIONS_DATA } from '../../../graphql/queries/commonQueries';
import ShowToast, { ToastTypeEnum } from '../../../common/components/ShowToast';
import { SolutionListItem } from '../Types/diagram-type';

interface UseFetchAISolutionsProps {
  diagramId?: string;
  selectedCardId?: string;
  activeModal: 'aiSolution' | 'processInfo' | 'diagram' | null;
}

export const useFetchAISolutions = ({
  diagramId,
  selectedCardId,
  activeModal,
}: UseFetchAISolutionsProps) => {
  const [solutionData, setSolutionData] = useState<SolutionListItem[]>([]);
  const [dataSourceType, setDataSourceType] = useState<'rest' | 'graphql' | null>(null);
  const [solutionsLoading, setSolutionsLoading] = useState(false);

  // GraphQL query
  const {
    data,
    error: solutionsError,
    loading: qlLoading,
  } = useGraphQLQuery(
    GET_AI_SOLUTIONS_DATA,
    {
      v1: { subprocessId: `${selectedCardId}` },
      v2: ['name'],
    },
    { skip: !activeModal || activeModal !== 'aiSolution' }
  );

  // REST API fetch
  const fetchAISolution = useCallback(async (): Promise<any> => {
    if (!diagramId) return null;

    try {
      const url = `${API_BASE}/api/v1/e2e_taxonomy/opportunities/?diagram_id=${diagramId}`;
      const response = await httpService<any>(httpConstants.METHOD_TYPE.GET, url);

      if (!response) throw new Error('Invalid response structure');
      return response;
    } catch (err) {
      console.error('Error fetching solution by ID (REST):', err);
      return null;
    }
  }, [diagramId]);

  // Main loading effect
  useEffect(() => {
    const loadSolutions = async () => {
      if (activeModal !== 'aiSolution') return;

      setSolutionsLoading(true);

      try {
        // Try REST API first
        const response = await fetchAISolution();
        if (response?.results?.length > 0) {
          setSolutionData(
            response.results.map(
              ({
                id,
                opportunity_name,
                impact,
                vault,
              }: {
                id: string;
                opportunity_name: string;
                impact?: string;
                vault?: boolean;
              }) => ({
                id,
                opportunity_name,
                impact: impact || null,
                vault: vault || false,
              })
            )
          );
          setDataSourceType('rest');
          return;
        }

        // Fall back to GraphQL if REST fails
        if (qlLoading) {
          setSolutionsLoading(true);
        }
        if (data?.aiSolution?.fetch?.length) {
          setSolutionData(
            data.aiSolution.fetch.map(
              ({
                id,
                name,
                impactAssessment,
              }: {
                id: string;
                name: string;
                impactAssessment?: { general: string };
              }) => ({
                id,
                opportunity_name: name,
                impact: impactAssessment?.general ?? 'N/A',
              })
            )
          );
          setDataSourceType('graphql');
        } else if (!qlLoading) {
          setSolutionData([]);
          setDataSourceType(null);
        }
      } catch (err) {
        console.error('Error loading solutions:', err);
        ShowToast({ message: 'Cannot Load Solutions', type: ToastTypeEnum.Error });
      } finally {
        setSolutionsLoading(false);
      }
    };

    loadSolutions();
  }, [activeModal, data, solutionsError, fetchAISolution, qlLoading]);

  return {
    solutionData,
    setSolutionData,
    dataSourceType,
    solutionsLoading,
  };
};
